import React from 'react';
import { motion } from 'framer-motion';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
interface Example {
  src: string;
  alt: string;
  brand: string;
  title: string;
  span: string;
  highlight?: boolean;
}
const EXAMPLES: Example[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/v8fWYPFpJwfo81j5rqFNbs/Screenshot_2026-09-10_at_15.12.47.png',
  alt: 'Amoxil teaser Snak opening on a mother and child with the line “Two nights without sleep. Seven minutes to decide.”',
  brand: 'Amoxil',
  title: 'Story-led teaser opening',
  span: 'lg:col-span-2 lg:row-span-2',
  highlight: true
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2WDtchn43w8oePYBwCWGRa/Screenshot_2026-09-11_at_10.23.04.png',
  alt: 'Augmentin teaser Snak landing screen “Healing everyday heroes” with product information and a prescribing-information QR code',
  brand: 'Augmentin',
  title: 'Healing everyday heroes',
  span: 'lg:col-span-5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/muBayMeNnkB4SEKMGR337D/Screenshot_2026-09-11_at_10.21.21.png',
  alt: 'Seretide teaser Snak screen showing that 57% of patients believe their asthma is well controlled despite ongoing symptoms',
  brand: 'Seretide',
  title: 'Perception vs reality',
  span: 'lg:col-span-5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mNNe6DAiY2STPGnGqjkrgg/Screenshot_2026-09-11_at_10.16.34.png',
  alt: 'Seretide teaser Snak answer reveal confirming a correct response with referenced symptom statistics',
  brand: 'Seretide',
  title: 'Answer reveal with evidence',
  span: 'lg:col-span-7'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fwxxMaVeRAXkKjUX1mTrmd/Screenshot_2026-09-10_at_15.33.44.png',
  alt: 'Seretide teaser Snak animation explaining that not all inhalers are handled correctly and that effective therapy depends on real-world treatment use',
  brand: 'Seretide',
  title: 'Animated insight',
  span: 'lg:col-span-3'
}];
export function SlideSnakExamples({}: SlideProps) {
  return <InteriorSlide surface data-id="element-598" data-mp="3jtgz4ias22jp">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(0.6rem,1.3vw,1.25rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-599" data-mp="3jua8zs519862">
        <div data-id="element-600" data-mp="ao0r576ica6j">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-601" data-mp="aninhphimtol">
            Teaser examples
          </p>
          <h2 className="text-[clamp(1.8rem,3vw,2.9rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-602" data-mp="6kcliu1fronx">
            What a teaser Snak looks like{' '}
            <span className="text-[var(--brand)]" data-id="element-603" data-mp="fg1zwj4gvirt">in practice</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-604" data-mp="uct2105iz8l4">
          Brand-compliant and fully referenced, on phone and on desktop.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.5rem,1vw,1rem)] sm:grid-cols-2 lg:grid-cols-12 lg:grid-rows-2" data-id="element-605" data-mp="18z87927p8sg0">
        {EXAMPLES.map((example, index) => <motion.figure key={example.src} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.14 + index * 0.09,
        duration: 0.5
      }} className={`flex min-h-0 flex-col overflow-hidden rounded-2xl border bg-white p-[clamp(0.4rem,0.7vw,0.7rem)] shadow-sm ${example.span} ${example.highlight ? 'border-[var(--brand)]/40 ring-1 ring-[var(--brand)]/15' : 'border-[var(--fg)]/8'}`} data-id="element-606" data-mp="yzheb4yseli1" data-mpi={index}>
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-[var(--surface)]" data-id="element-607" data-mp="l71jgrg1hts7">
              <img src={example.src} alt={example.alt} className="h-full w-full object-contain" data-id="element-608" data-mp="bbhny65e0x3z" />
            </div>

            <figcaption className="flex flex-shrink-0 items-baseline justify-between gap-2 pt-2" data-id="element-609" data-mp="1enotejexnn13">
              <div className="min-w-0" data-id="element-610" data-mp="19p554ccv9z5k">
                <p className="truncate text-[clamp(0.7rem,0.95vw,0.88rem)] font-extrabold leading-tight text-[var(--fg)]" data-id="element-611" data-mp="14v0tvgwkj4yo">
                  {example.title}
                </p>
                <p className="mt-0.5 truncate text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--muted)]" data-id="element-612" data-mp="uxmnka4czwk3">
                  {example.brand}
                </p>
              </div>
              <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${example.highlight ? 'bg-[var(--brand)] text-white' : 'bg-[var(--brand)]/10 text-[var(--brand)]'}`} data-id="element-613" data-mp="lwybhkyj069u">
                Teaser
              </span>
            </figcaption>
          </motion.figure>)}
      </div>
    </InteriorSlide>;
}